import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/auth-store';

/**
 * EnrollLaptopPage — "My Account" → "Enroll my laptop"
 *
 * Staff-facing landing page for the optiedge-provisioner agent. Shows the
 * currently-signed-in staff member their subnet/role mapping, provides the
 * agent download link, and walks through the three-step CLI flow.
 *
 * v0.1.0-staff-alpha scope: this is a static instructions page. Future
 * enhancements (copy-enrollment-URL button, paste-this-token auto-flow for
 * the agent's webui, installer download with pre-signed identity) land when
 * PKCE + systray + webui ship in the agent.
 */
export function EnrollLaptopPage() {
  const { t } = useTranslation();
  const adminUser = useAuthStore((s) => s.adminUser);

  const role = adminUser?.role ?? '';
  const email = adminUser?.email ?? '';
  const subnet = roleToSubnet(role);
  const downloadUrl = import.meta.env.VITE_OPTIEDGE_AGENT_DOWNLOAD_URL ?? '';

  return (
    <div className="mx-auto max-w-3xl space-y-8 p-8">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold text-foreground">{t('Enroll my laptop')}</h1>
        <p className="text-sm text-muted-foreground">
          {t(
            'Install the OptiEdge agent on your laptop to join the OptiSigns corporate VPN. Once connected, you can SSH into customer devices to troubleshoot.',
          )}
        </p>
      </header>

      <section className="rounded-lg border bg-muted/30 p-6" data-testid="enroll-identity">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t('Signed in as')}
        </h2>
        <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <dt className="text-muted-foreground">{t('Email')}</dt>
          <dd className="font-mono text-foreground">{email || '—'}</dd>
          <dt className="text-muted-foreground">{t('Role')}</dt>
          <dd className="font-mono text-foreground">{role || '—'}</dd>
          <dt className="text-muted-foreground">{t('Will join subnet')}</dt>
          <dd className="font-mono text-foreground">{subnet}</dd>
        </dl>
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-foreground">{t('Setup (3 steps)')}</h2>

        <ol className="space-y-5">
          <Step
            n={1}
            title={t('Download the agent')}
            body={
              <>
                <p className="text-sm text-muted-foreground">
                  {t('macOS binary for the current v0.1.0-staff-alpha milestone.')}
                </p>
                {downloadUrl ? (
                  <a
                    href={downloadUrl}
                    className="mt-2 inline-block rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                    data-testid="download-agent"
                  >
                    {t('Download optiedge (macOS)')}
                  </a>
                ) : (
                  <p className="mt-2 rounded-md border border-amber-400/40 bg-amber-50 p-3 text-xs text-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
                    {t(
                      'Download URL not yet configured. Ask Henry for the dev build — the distribution pipeline lands in Phase 5.',
                    )}
                  </p>
                )}
              </>
            }
          />

          <Step
            n={2}
            title={t('Install + run')}
            body={
              <>
                <p className="text-sm text-muted-foreground">
                  {t(
                    'Unpack the tarball then run the agent. Dev builds need sudo for the userspace TUN.',
                  )}
                </p>
                <pre className="mt-2 overflow-x-auto rounded-md border bg-background p-3 font-mono text-xs">
                  {`# Example:
tar -xzf optiedge-sandbox.tar.gz
sudo ./optiedge up --staff --firebase-token "<paste Firebase ID token>"`}
                </pre>
              </>
            }
          />

          <Step
            n={3}
            title={t('Verify the tunnel')}
            body={
              <>
                <p className="text-sm text-muted-foreground">
                  {t(
                    'Once the agent reports "● Connected" you can ping the VPN server. Your prod WG tunnel (if any) stays untouched on a separate utun interface.',
                  )}
                </p>
                <pre className="mt-2 overflow-x-auto rounded-md border bg-background p-3 font-mono text-xs">
                  {`ping 100.127.0.1`}
                </pre>
              </>
            }
          />
        </ol>
      </section>

      <footer className="rounded-lg border bg-muted/30 p-4 text-xs text-muted-foreground">
        <p>
          <strong>{t('How this maps to the VPN:')}</strong>{' '}
          {t(
            'Your peer record is created in the sandbox (100.127.0.0/16) or prod (100.64.0.0/10) overlay depending on which build you installed. Your email and role are attached to the peer — the staff console shows them in the Network → Peers view.',
          )}
        </p>
      </footer>
    </div>
  );
}

interface StepProps {
  n: number;
  title: string;
  body: React.ReactNode;
}

function Step({ n, title, body }: StepProps) {
  return (
    <li className="flex gap-4">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
        {n}
      </div>
      <div className="flex-1 space-y-1">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {body}
      </div>
    </li>
  );
}

/**
 * roleToSubnet — client-side display copy of the server-side map in
 * staff-enroll.service.ts. Shown here so staff know which subnet they'll
 * land on BEFORE they run the agent. Must stay in sync with the server map:
 *   - root, engineer → engineers
 *   - support, readonly → support
 */
function roleToSubnet(role: string): string {
  switch (role) {
    case 'root':
    case 'engineer':
      return 'engineers';
    case 'support':
    case 'readonly':
      return 'support';
    default:
      return '—';
  }
}
